import type { ReactNode } from 'react'

/**
 * Small shared primitives. Deliberately plain Tailwind rather than a component
 * library — §10 allows either, and the surface here is small enough that a
 * dependency would cost more than it saves.
 *
 * Sizing follows §9: generous whitespace, readable at arm's length, touch
 * targets big enough to hit on a phone one-handed.
 */

export function Button({
  variant = 'secondary',
  className = '',
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'secondary' | 'danger' | 'ghost'
}) {
  const styles = {
    primary: 'bg-slate-900 text-white hover:bg-slate-700 disabled:bg-slate-300',
    secondary:
      'border border-slate-300 bg-white text-slate-900 hover:bg-slate-50 disabled:text-slate-400',
    danger:
      'border border-red-300 bg-white text-red-700 hover:bg-red-50 disabled:text-red-300',
    ghost: 'text-slate-600 hover:bg-slate-100 hover:text-slate-900',
  }[variant]

  return (
    <button
      {...props}
      className={`inline-flex min-h-11 items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900 disabled:cursor-not-allowed ${styles} ${className}`}
    />
  )
}

export function Field({
  label,
  hint,
  error,
  required,
  children,
}: {
  label: string
  hint?: string | undefined
  error?: string | undefined
  required?: boolean
  children: ReactNode
}) {
  return (
    <label className="block space-y-1.5">
      <span className="block text-sm font-medium text-slate-800">
        {label}
        {required === true && <span className="text-red-600"> *</span>}
      </span>
      {children}
      {hint !== undefined && error === undefined && (
        <span className="block text-xs text-slate-500">{hint}</span>
      )}
      {error !== undefined && (
        <span role="alert" className="block text-xs font-medium text-red-700">
          {error}
        </span>
      )}
    </label>
  )
}

const inputBase =
  'block w-full min-h-11 rounded-lg border border-slate-300 bg-white px-3 py-2 text-slate-900 shadow-sm transition placeholder:text-slate-400 focus:border-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-900/10 disabled:bg-slate-50'

export function Input({
  className = '',
  invalid,
  ...props
}: React.InputHTMLAttributes<HTMLInputElement> & {
  invalid?: boolean
  // React 19 passes `ref` as an ordinary prop to function components, so it
  // rides along in the spread — but the HTML attribute type doesn't declare it.
  ref?: React.Ref<HTMLInputElement>
}) {
  return (
    <input
      {...props}
      aria-invalid={invalid === true ? true : undefined}
      className={`${inputBase} ${invalid === true ? 'border-red-400' : ''} ${className}`}
    />
  )
}

export function Select({
  className = '',
  ...props
}: React.SelectHTMLAttributes<HTMLSelectElement>) {
  return <select {...props} className={`${inputBase} ${className}`} />
}

export function Textarea({
  className = '',
  ...props
}: React.TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea {...props} className={`${inputBase} min-h-24 ${className}`} />
}

export function Card({
  className = '',
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      {...props}
      className={`rounded-xl border border-slate-200 bg-white p-5 shadow-sm ${className}`}
    />
  )
}

export function EmptyState({
  title,
  children,
}: {
  title: string
  children?: ReactNode
}) {
  return (
    <div className="rounded-xl border border-dashed border-slate-300 bg-white px-6 py-10 text-center">
      <p className="font-medium text-slate-900">{title}</p>
      {children !== undefined && (
        <div className="mt-2 text-sm text-slate-600">{children}</div>
      )}
    </div>
  )
}

export function ErrorNote({ children }: { children: ReactNode }) {
  return (
    <p
      role="alert"
      className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800"
    >
      {children}
    </p>
  )
}

/** Dollar figure, right-aligned with tabular numerals so columns line up. */
export function Money({
  value,
  className = '',
}: {
  value: number
  className?: string
}) {
  return (
    <span className={`tabular ${className}`}>
      {value < 0 && '−'}
      {Math.abs(value).toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
      })}
    </span>
  )
}

export function Badge({
  tone = 'neutral',
  children,
}: {
  tone?: 'neutral' | 'warn' | 'danger' | 'ok'
  children: ReactNode
}) {
  const styles = {
    neutral: 'bg-slate-100 text-slate-700',
    ok: 'bg-emerald-100 text-emerald-800',
    warn: 'bg-amber-100 text-amber-900',
    danger: 'bg-red-100 text-red-800',
  }[tone]
  return (
    <span
      className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-medium ${styles}`}
    >
      {children}
    </span>
  )
}
